import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { safeGetItem } from '@/lib/safeBrowser';

const initialLang = safeGetItem('app-language', 'pt-BR') || 'pt-BR';

// Lazy-load only the active language; defer the other
async function loadLocale(lang: string) {
  if (lang === 'pt-BR') {
    return (await import('./locales/pt-BR.json')).default;
  }
  return (await import('./locales/en.json')).default;
}

// Load initial language synchronously via eager import to avoid flash
const initialResources: Record<string, { translation: any }> = {};

// We start with an empty bundle and load async immediately
i18n
  .use(initReactI18next)
  .init({
    resources: initialResources,
    lng: initialLang,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    // Return key while loading
    returnNull: false,
    parseMissingKeyHandler: (key: string, defaultValue?: string) => defaultValue || key,
  });

// Load the active language immediately
loadLocale(initialLang).then(translations => {
  i18n.addResourceBundle(initialLang, 'translation', translations, true, true);
  // Trigger re-render for components waiting on translations
  i18n.changeLanguage(initialLang);
});

// Preload the other language after a short delay (non-blocking)
const otherLang = initialLang === 'pt-BR' ? 'en' : 'pt-BR';
setTimeout(() => {
  loadLocale(otherLang).then(translations => {
    i18n.addResourceBundle(otherLang, 'translation', translations, true, true);
  });
}, 3000);

// Dev helper: collect missing i18n keys to speed up coverage fixes.
if (typeof window !== 'undefined') {
  const STORAGE_KEY = 'i18n-missing-keys';

  const recordMissingKey = (key: string) => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const existing = new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
      if (!existing.has(key)) {
        existing.add(key);
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(existing).sort()));
      }
    } catch {
      // ignore storage errors
    }
  };

  i18n.on('missingKey', (_lngs, _ns, key) => {
    recordMissingKey(key);
    // eslint-disable-next-line no-console
    console.warn(`[i18n] Missing key: ${key}`);
  });
}

export default i18n;
